import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Home,
  FileText,
  Calculator,
  Users,
  Shield,
  Clock,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  BarChart3,
  Calendar,
  Menu,
  X,
  Star,
  ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  { icon: Home, title: "Residential Mortgages", desc: "First-time buyers to remortgages, we find the perfect deal for your home." },
  { icon: Calculator, title: "Budget Planning", desc: "AI-powered analysis of your finances to maximize affordability." },
  { icon: FileText, title: "Buy-to-Let", desc: "Expert advice on investment properties and portfolio mortgages." },
  { icon: Shield, title: "Protection", desc: "Life insurance, income protection, and building insurance solutions." },
  { icon: BarChart3, title: "Credit Analysis", desc: "Comprehensive credit report analysis with improvement recommendations." },
  { icon: Users, title: "Commercial Mortgages", desc: "Business premises and commercial property financing expertise." }
];

const stages = [
  { name: "Initial Consultation", desc: "Free consultation to understand your needs" },
  { name: "Fact Finding", desc: "Gather financial information and documents" },
  { name: "Document Collection", desc: "Collect all required documentation" },
  { name: "Research & Analysis", desc: "Affordability, lender criteria, ESIS" },
  { name: "Compliance Check", desc: "Ensure all requirements are met" },
  { name: "Lender Submission", desc: "Submit to the most suitable lenders" },
  { name: "Survey & Valuation", desc: "Property survey and valuation" },
  { name: "Offer Received", desc: "Mortgage offer from lender" },
  { name: "Solicitor Handover", desc: "Legal work begins" },
  { name: "Legal Completion", desc: "Contracts exchanged" },
  { name: "Protection Review", desc: "Ensure adequate protection" },
  { name: "Completion", desc: "Keys in hand!" }
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white relative noise-overlay">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#020617]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-amber-500 rounded-lg flex items-center justify-center">
                <Home className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg tracking-tight">Thames Mortgage & Protection</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-sm text-slate-300 hover:text-white transition">Services</a>
              <Link to="/advisors" className="text-sm text-slate-300 hover:text-white transition">Advisors</Link>
              <a href="#process" className="text-sm text-slate-300 hover:text-white transition">Process</a>
              <a href="#contact" className="text-sm text-slate-300 hover:text-white transition">Contact</a>
              <a href="https://portal.myac.re/ory-login" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" className="text-slate-300 hover:text-white">Client Portal</Button>
              </a>
              <Link to="/book-appointment">
                <Button className="bg-blue-600 hover:bg-blue-700 glow-blue" data-testid="book-consultation-btn">
                  Book Consultation
                </Button>
              </Link>
            </div>

            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-[#0f172a] border-b border-white/10 px-4 py-4"
          >
            <div className="flex flex-col gap-4">
              <a href="#services" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <Link to="/advisors" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Advisors</Link>
              <a href="#process" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Process</a>
              <a href="#contact" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Contact</a>
              <a href="https://portal.myac.re/ory-login" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white">Client Portal</a>
              <Link to="/book-appointment">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">Book Consultation</Button>
              </Link>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1762983166320-8e301ab178f7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920" 
            alt="London skyline with Tower Bridge and River Thames"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/80 via-[#020617]/60 to-[#020617]"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/30 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/30 rounded-full px-4 py-1.5 mb-6">
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                <span className="text-amber-400 text-sm font-medium tracking-wide">FCA Regulated - FRN 1045227</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] mb-6" data-testid="hero-heading">
                Expert Mortgage
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-amber-400 bg-clip-text text-transparent">Advice You Can</span>
                <br />
                Trust
              </h1>
              
              <p className="text-lg text-slate-300 max-w-lg leading-relaxed mb-8">
                Professional mortgage advisory tailored to your circumstances. From first-time buyers to complex remortgages, we guide you through every step.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to="/book-appointment">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base rounded-xl glow-blue" data-testid="hero-book-btn">
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Consultation
                  </Button>
                </Link>
                <a href="tel:02080506062">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-base rounded-xl" data-testid="hero-call-btn">
                    <Phone className="w-5 h-5 mr-2" />
                    02080506062
                  </Button>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />
                  ))}
                  <span className="text-slate-400 text-sm ml-2">5 Star Rated</span>
                </div>
                <div className="w-px h-6 bg-slate-600 hidden sm:block"></div>
                <div className="text-center">
                  <span className="text-white font-bold">100+</span>
                  <span className="text-slate-400 text-sm ml-1">Happy Clients</span>
                </div>
                <div className="w-px h-6 bg-slate-600 hidden sm:block"></div>
                <div className="text-center">
                  <span className="text-white font-bold">£1M+</span>
                  <span className="text-slate-400 text-sm ml-1">Arranged</span>
                </div>
              </div>
            </motion.div>

            {/* Right: Floating Cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block"
            >
              <div className="relative">
                {/* Main Feature Card */}
                <motion.div 
                  animate={{ y: [0, -8, 0] }} 
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 mb-4"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                      <Home className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Residential Mortgages</h3>
                      <p className="text-sm text-slate-400">First-time buyers & remortgage</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-xs text-blue-300">Fixed Rate</span>
                    <span className="px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full text-xs text-green-300">Variable</span>
                    <span className="px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-xs text-amber-300">Tracker</span>
                  </div>
                </motion.div>

                <div className="grid grid-cols-2 gap-4">
                  <motion.div 
                    animate={{ y: [0, -6, 0] }} 
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                    className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5"
                  >
                    <Shield className="w-8 h-8 text-rose-400 mb-3" />
                    <h4 className="font-semibold text-white text-sm mb-1">Protection</h4>
                    <p className="text-xs text-slate-400">Life & income cover</p>
                  </motion.div>
                  
                  <motion.div 
                    animate={{ y: [0, -6, 0] }} 
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5"
                  >
                    <Calculator className="w-8 h-8 text-cyan-400 mb-3" />
                    <h4 className="font-semibold text-white text-sm mb-1">Buy-to-Let</h4>
                    <p className="text-xs text-slate-400">Investment properties</p>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-amber-500 font-medium mb-4 tracking-wide uppercase text-sm">What We Offer</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Comprehensive Mortgage Services</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Expert guidance through every step of your mortgage journey with transparent pricing and dedicated support.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-[#0f172a] border-slate-800 hover:border-blue-500/50 transition-all duration-300 h-full group">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition">
                      <service.icon className="w-6 h-6 text-blue-500" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{service.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 px-4 md:px-8 bg-[#0f172a]/50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-amber-500 font-medium mb-4 tracking-wide uppercase text-sm">Our Process</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">From Application to Completion</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Track your mortgage application through every stage with real-time updates and transparent communication.</p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-blue-500 via-amber-500 to-green-500 hidden lg:block"></div>
            <div className="space-y-6">
              {stages.map((stage, index) => (
                <motion.div
                  key={stage.name}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <Card className="bg-[#0f172a] border-slate-800 inline-block">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-xl font-bold font-mono text-blue-500">{String(index + 1).padStart(2, '0')}</span>
                          <h3 className="font-semibold text-sm">{stage.name}</h3>
                        </div>
                        <p className="text-slate-400 text-xs">{stage.desc}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="hidden lg:flex w-3 h-3 bg-blue-500 rounded-full ring-4 ring-[#020617]"></div>
                  <div className="flex-1 hidden lg:block"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features & Rates Section - Side by Side */}
      <section id="rates" className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            {/* AI-Powered Analysis */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#0f172a] border border-slate-800 rounded-2xl p-8 flex flex-col"
            >
              <p className="text-amber-500 font-medium mb-4 tracking-wide uppercase text-sm">Protection Services</p>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">Protection & Insurance Review</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Comprehensive protection advice to secure your family's financial future and safeguard your home investment.
              </p>
              <div className="space-y-3 flex-grow">
                {[
                  "Life Cover & Critical Illness Protection",
                  "Income Protection Insurance",
                  "Buildings & Contents Insurance",
                  "Solicitor & Legal Fee Guidance",
                  "Family Takaful (Islamic Protection)"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-rose-500 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <Link to="/protection-guide">
                <Button className="mt-6 bg-rose-600 hover:bg-rose-700 w-full" data-testid="protection-review-btn">
                  View Protection Guide <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>

            {/* Best Rate Table */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#0f172a] border border-slate-800 rounded-2xl p-8 flex flex-col"
            >
              <p className="text-amber-500 font-medium mb-4 tracking-wide uppercase text-sm">Live Rates</p>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">Today's Best Mortgage Rates</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Compare rates from leading UK lenders. Rates are indicative and subject to individual circumstances.
              </p>
              <div className="flex-grow flex flex-col justify-center space-y-4">
                {[
                  "Compare 100+ mortgage products",
                  "Fixed, tracker & variable rates",
                  "First-time buyer deals",
                  "Remortgage offers",
                  "Buy-to-let rates"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-purple-500 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <Link to="/best-rates">
                <Button className="mt-6 bg-purple-600 hover:bg-purple-700 w-full" data-testid="view-rates-btn">
                  View Best Rates <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 md:px-8 bg-[#0f172a]/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-amber-500 font-medium mb-4 tracking-wide uppercase text-sm">Get in Touch</p>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Start Your Journey?</h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Contact us today for a free, no-obligation consultation. Our expert advisors are ready to help you find the perfect mortgage solution.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Call us</p>
                    <p className="font-semibold">02080506062</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Email us</p>
                    <p className="font-semibold">admin@thamesresi.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Visit us</p>
                    <p className="font-semibold">Unit 4.1, Town Quay Wharf<br/>Abbey Road, Barking, IG11 7BZ</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Opening hours</p>
                    <p className="font-semibold">Mon-Fri: 9am - 6pm, Sat: 10am - 2pm</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-[#0f172a] border-slate-800">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-6">Send us a message</h3>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <Input
                        placeholder="Your name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="bg-[#020617] border-slate-700 focus:border-blue-500"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Your email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="bg-[#020617] border-slate-700 focus:border-blue-500"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <Input
                        type="tel"
                        placeholder="Your phone"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        required
                        className="bg-[#020617] border-slate-700 focus:border-blue-500"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Textarea
                        placeholder="Your message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                        rows={4}
                        className="bg-[#020617] border-slate-700 focus:border-blue-500"
                        data-testid="contact-message-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={submitting}
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-amber-500 rounded-lg flex items-center justify-center">
                  <Home className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold">Thames Mortgage & Protection</span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Thames Mortgage & Protection Ltd is a UK finance specialist in property & business based finance for all types of commercial and personal investments. We are a credit broker not a lender.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-slate-400 text-sm">
                <p>Unit 4.1, Town Quay Wharf</p>
                <p>Abbey Road, Barking, IG11 7BZ</p>
                <p>Email: admin@thamesresi.com</p>
                <p>Tel: 02080506062</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <div className="space-y-2">
                <Link to="/complaints-policy" className="block text-slate-400 hover:text-white text-sm">Complaints Policy</Link>
                <Link to="/privacy-policy" className="block text-slate-400 hover:text-white text-sm">Privacy Policy</Link>
                <Link to="/terms-conditions" className="block text-slate-400 hover:text-white text-sm">Terms & Conditions</Link>
                <Link to="/cookie-policy" className="block text-slate-400 hover:text-white text-sm">Cookie Policy</Link>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8">
            <div className="text-slate-500 text-xs leading-relaxed space-y-2">
              <p>© 2026 Thames Mortgage & Protection Ltd. All rights reserved.</p>
              <p>Thames Mortgage & Protection Ltd is registered in England and Wales No. 16801243. Registered Office: Unit 4.1, Town Quay Wharf, Abbey Road, Barking, IG11 7BZ.</p>
              <p>Thames Mortgage & Protection Ltd is authorised and regulated by the Financial Conduct Authority FRN 1045227. You can check this on the Financial Services Register by visiting the FCA's website <a href="https://www.fca.org.uk/register" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">www.fca.org.uk/register</a></p>
              <p>Not all of our products are regulated by the Financial Conduct Authority – please ask for details.</p>
              <p className="font-medium text-amber-500">Your home may be repossessed if you do not keep up repayments on your mortgage or loans secured on it.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
